// src/views/Settings/user/ProfileSection.jsx
//
// Los datos del usuario, y nada más. Salió tal cual de `views/Profile/Profile.jsx`,
// que mezclaba tres alcances bajo un subtítulo que prometía "personal
// information": el heatmap de transacciones y la lista de proyectos son de la
// cuenta, no de la persona, y se fueron al alcance que los posee.
//
// La prueba: tu nombre, tu foto y tu bio te siguen cambies de organización o no.
import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Calendar, FileText, AtSign, Pencil, X } from 'lucide-react';
import AuthContext from '../../../AuthContext';
import AuthService from '../../../services/auth.service';
import { generateImageDataUrl } from '../../../services/imageGeneration';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../../../components/ui/Skeleton';
import SectionHeader from '../SectionHeader';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import './ProfileSection.css';

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#1f1f1f',
    borderColor: state.isFocused ? '#6B3FC1' : '#374151',
    color: '#fff',
    boxShadow: state.isFocused ? '0 0 0 1px #6B3FC1' : 'none',
    '&:hover': {
      borderColor: '#6B3FC1'
    }
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#1f1f1f'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#6B3FC1'
      : state.isFocused
      ? '#27272a'
      : 'transparent',
    color: state.isSelected ? '#fff' : '#e5e7eb',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#6B3FC1'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: '#fff'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af'
  })
};

export default function ProfileSection() {
  const { isAuthenticated, isLoading, sessionId, updateUser } = useContext(AuthContext);
  const { currentAccount } = useOrganization();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { t } = useTranslation();

  // State initialization
  const [editingField, setEditingField] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [modalTab, setModalTab] = useState('upload');
  const [uploadError, setUploadError] = useState(null);
  const [avatarPrompt, setAvatarPrompt] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    bio: '',
    phone_number: '',
    country: '',
    birth_date: ''
  });
  const [updateError, setUpdateError] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Country list for react-select
  const countryOptions = useMemo(() => countryList().getData(), []);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // User profile data (REST via AuthService)
  const [profileError, setProfileError] = useState(null);
  const [data, setData] = useState(null);
  // "Ya hay respuesta", no "hay una petición en vuelo": es lo que decide si
  // se puede pintar el perfil o todavía toca el esqueleto.
  const [profileSettled, setProfileSettled] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      setProfileError(null);
      try {
        const profile = await AuthService.getUserProfile();
        if (isMounted) {
          setData({ getUserProfile: profile });
          setProfileSettled(true);
        }
      } catch (err) {
        if (isMounted) {
          setProfileError(err);
          setProfileSettled(true);
        }
      }
    };
    if (isAuthenticated && sessionId) {
      fetchProfile();
    }
    return () => { isMounted = false; };
  }, [isAuthenticated, sessionId]);

  // Profile update loading state
  const [updateLoading, setUpdateLoading] = useState(false);

  // Avatar generation (Image Service) state
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  // Initialize form data from API response
  useEffect(() => {
    if (data?.getUserProfile) {
      const profile = data.getUserProfile;
      setFormData({
        bio: profile.bio || '',
        phone_number: profile.phone_number || profile.phone || '',
        country: profile.country || '',
        birth_date: profile.birth_date || ''
      });

      // Find matching country option
      const foundOption = countryOptions.find(
        (opt) => opt.value === profile.country
      );
      setSelectedCountry(foundOption || null);

      if (typeof updateUser === 'function') {
        const avatar = profile.avatar_url || profile.avatarUrl || profile.avatar || '';
        updateUser({
          avatar_url: avatar,
          avatarUrl: avatar,
          avatar: avatar,
          username: profile.username,
          email: profile.email,
          name: profile.name,
          last_name: profile.last_name,
        });
      }
    }
  }, [data, countryOptions, updateUser]);

  const applyAvatarUrlToProfile = (avatarUrl) => {
    const cacheBust = Date.now();
    const avatarWithVersion = avatarUrl
      ? `${avatarUrl}${avatarUrl.includes('?') ? '&' : '?'}v=${cacheBust}`
      : '';

    setData((prev) => {
      const currentProfile = prev?.getUserProfile || {};
      return {
        getUserProfile: {
          ...currentProfile,
          avatarUrl: avatarWithVersion,
          avatar_url: avatarWithVersion
        }
      };
    });

    if (typeof updateUser === 'function') {
      updateUser({
        avatar_url: avatarWithVersion,
        avatarUrl: avatarWithVersion,
        avatar: avatarWithVersion,
        avatar_cache_bust: cacheBust,
      });
    }
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read image file'));
      reader.readAsDataURL(file);
    });

  // Sin projectId: una foto de perfil no vive en ningún proyecto. Antes se
  // mandaba projects[0].id sólo para satisfacer a la API, archivando la imagen
  // bajo el proyecto que ordenara primero. El backend ya no lo pide (!859).
  const handleGenerateAvatar = async () => {
    if (!avatarPrompt.trim()) return;

    const accountId = currentAccount?.id;
    if (!accountId) {
      setGenerateError('No se encontró una cuenta activa.');
      return;
    }

    try {
      setGenerateError(null);
      setGeneratedImageUrl(null);
      setGenerateLoading(true);

      const dataUrl = await generateImageDataUrl({ accountId, prompt: avatarPrompt });
      setGeneratedImageUrl(dataUrl);
    } catch (err) {
      console.error('Error generando avatar:', err);
      setGenerateError(err.message || 'Error generando la imagen.');
    } finally {
      setGenerateLoading(false);
    }
  };

  // Save generated avatar handler.
  // generateImageDataUrl already inlined the image — the URL the service hands
  // back is presigned and expires in 24h, so it is never what gets stored.
  const handleSaveGeneratedImage = async () => {
    if (!generatedImageUrl) return;

    try {
      setAvatarUploading(true);
      const updatedProfile = await AuthService.uploadAvatar(generatedImageUrl);
      const nextAvatarUrl =
        updatedProfile.avatarUrl ||
        updatedProfile.avatar_url ||
        updatedProfile.avatar ||
        generatedImageUrl;

      applyAvatarUrlToProfile(nextAvatarUrl);
      setShowModal(false);
    } catch (err) {
      console.error('Error saving generated avatar:', err);
      setUploadError(err.message || 'Failed to save generated avatar. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  // Handle file drop for avatar upload
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadError(null);

    const file = e.dataTransfer.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Handle file selection for avatar upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadError(null);
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Confirm file upload after preview
  const confirmUpload = async () => {
    if (!selectedFile) return;
    
    try {
      await handleFileUpload(selectedFile);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadError('Failed to upload image. Please try again.');
    }
  };

  // Cancel upload after preview
  const cancelUpload = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
  };

  // Upload file to S3
  const handleFileUpload = async (file) => {
    try {
      setUploadError(null);
      setAvatarUploading(true);
      const avatarData = await fileToDataUrl(file);
      const updatedProfile = await AuthService.uploadAvatar(avatarData);
      const nextAvatarUrl =
        updatedProfile.avatarUrl ||
        updatedProfile.avatar_url ||
        updatedProfile.avatar ||
        previewUrl ||
        '';

      applyAvatarUrlToProfile(nextAvatarUrl);
      setShowModal(false);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  // Remove avatar handler
  const handleRemoveAvatar = async () => {
    try {
      setAvatarUploading(true);
      await AuthService.updateProfile({ avatar_url: null });
      applyAvatarUrlToProfile('');
    } catch (error) {
      console.error('Error removing avatar:', error);
      setUploadError(error.message || 'Failed to remove avatar. Please try again.');
    } finally {
      setAvatarUploading(false);
    }
  };

  // Set field to edit mode
  const handleFieldClick = (fieldName) => {
    setEditingField(fieldName);
  };

  // Handle form field changes
  const handleInputChange = (e, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  // Handle country selection change
  const handleCountryChange = (option) => {
    setSelectedCountry(option);
    setFormData((prev) => ({
      ...prev,
      country: option?.value || ''
    }));
  };


  // Save profile changes
  const handleSaveChanges = async () => {
    try {
      setUpdateLoading(true);
      setUpdateError(null);

      // Preparar datos para enviar (solo campos no vacíos)
      const dataToUpdate = {};
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          dataToUpdate[key] = formData[key];
        }
      });

      const updatedProfile = await AuthService.updateProfile(dataToUpdate);
      if (typeof updateUser === 'function') {
        updateUser(updatedProfile);
      }
      
      // Actualizar el estado local con los datos actualizados
      setData({ getUserProfile: { ...data.getUserProfile, ...updatedProfile } });
      setEditingField(null);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      setUpdateError(error.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Sin pantalla de carga. La sección se pinta ya y los datos entran cuando
  // llegan: `userProfile` es `{}` mientras tanto y cada campo tiene su propio
  // vacío. Un cargando a pantalla completa aquí borraba el menú lateral y lo
  // devolvía medio segundo después, que se lee como un salto, no como una
  // espera — y la espera es de un solo fetch.

  if (profileError) {
    return (
      <div className="error-message">
        {profileError.message || 'Error loading profile'}
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const userProfile = data?.getUserProfile || {};
  // Hero meta: phone + resolved country name (from the saved profile).
  const heroPhone = userProfile.phone_number || userProfile.phone || '';
  const heroCountry =
    countryOptions.find((o) => o.value === userProfile.country)?.label ||
    userProfile.country ||
    '';
  const showSaveButton = editingField !== null;

  return (
    <>
      <div className="profile-main-content">
                <SectionHeader
                  title={t("profilePage.profileHeaderTitle")}
                  subtitle={t("profilePage.profileHeaderSubtitle")}
                />

                <div className="profile-main">
                  {/* Hero: identidad del usuario */}
                  {/* Mientras el perfil llega, un esqueleto con la forma que
                      va a tener. Quitar el cargando dejaba ver los componentes
                      vacíos y llenarse después, que es peor que esperar: la
                      pantalla se mueve dos veces. */}
                  {profileSettled ? (
                  <div className="profile-hero">
                    <div className="profile-hero-avatar">
                      <div className="avatar-ring">
                        <div className="avatar-circle">
                          {userProfile.avatarUrl ? (
                            <img
                              src={userProfile.avatarUrl}
                              alt="Profile"
                              className="avatar-image"
                              onError={(e) => {
                                console.error('Error loading image:', e);
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <User size={44} />
                          )}
                        </div>
                      </div>
                      <div className="avatar-buttons">
                        <button
                          className="profile-button primary"
                          onClick={() => {
                            setShowModal(true);
                            setModalTab('upload');
                            setGeneratedImageUrl(null);
                            setAvatarPrompt('');
                            setSelectedFile(null);
                            setPreviewUrl(null);
                            setGenerateError(null);
                            setUploadError(null);
                          }}
                          disabled={avatarUploading}
                        >
                          <Pencil size={14} />
                          {t("profilePage.avatar.changeBtn")}
                        </button>
                        <button
                          className="profile-button ghost"
                          onClick={handleRemoveAvatar}
                          disabled={avatarUploading}
                        >
                          {t("profilePage.avatar.removeBtn")}
                        </button>
                      </div>
                    </div>

                    <div className="profile-hero-info">
                      {/* El nombre y la acción general en la misma línea:
                          la tarjeta usaba media anchura y dejaba el resto
                          muerto. Se fue el badge que decía "Profile" estando
                          ya dentro de Settings › Profile: no era un estado
                          ni un rol, era la palabra repetida. */}
                      <div className="profile-hero-name">
                        <h2>{userProfile.username || userProfile.name || '—'}</h2>
                        <button
                          type="button"
                          className="profile-hero-edit"
                          onClick={() => setShowEditProfile(true)}
                        >
                          <Pencil size={14} />
                          {t("profilePage.editProfile", "Edit profile")}
                        </button>
                      </div>
                      {(userProfile.bio ||
                        userProfile.email ||
                        heroPhone ||
                        heroCountry) && (
                        <ul className="profile-hero-meta">
                          {userProfile.bio && (
                            <li className="profile-hero-bio-item">
                              <FileText size={14} />
                              <span>{userProfile.bio}</span>
                            </li>
                          )}
                          {userProfile.email && (
                            <li>
                              <Mail size={14} />
                              <span>{userProfile.email}</span>
                            </li>
                          )}
                          {heroPhone && (
                            <li>
                              <Phone size={14} />
                              <span>{heroPhone}</span>
                            </li>
                          )}
                          {heroCountry && (
                            <li>
                              <MapPin size={14} />
                              <span>{heroCountry}</span>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                  ) : (
                    <div className="profile-hero" aria-hidden="true">
                      <div className="profile-hero-avatar">
                        <Skeleton width="120px" height="120px" rounded="full" />
                      </div>
                      <div className="profile-hero-info space-y-3">
                        <Skeleton height="28px" width="220px" rounded="md" />
                        <Skeleton height="14px" width="280px" rounded="sm" />
                        <Skeleton height="14px" width="200px" rounded="sm" />
                        <Skeleton height="32px" width="130px" rounded="lg" />
                      </div>
                    </div>
                  )}

                </div>

                {/* Edit profile modal — los datos personales editables */}
                {showEditProfile && (
                  <div
                    className="profile-edit-overlay"
                    onClick={() => {
                      setEditingField(null);
                      setShowEditProfile(false);
                    }}
                  >
                    <div
                      className="profile-edit-modal"
                      onClick={(e) => e.stopPropagation()}
                      role="dialog"
                      aria-modal="true"
                    >
                      <div className="profile-edit-modal__head">
                        <h3>{t("profilePage.editProfile", "Edit profile")}</h3>
                        {editingField && (
                          <button
                            className="cancel-button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                [editingField]: userProfile[editingField] || '',
                              });
                              setEditingField(null);
                            }}
                            disabled={updateLoading}
                          >
                            {t("Cancel")}
                          </button>
                        )}
                        <button
                          type="button"
                          className="profile-edit-modal__close"
                          onClick={() => {
                            setEditingField(null);
                            setShowEditProfile(false);
                          }}
                          aria-label="Close"
                        >
                          <X size={18} />
                        </button>
                      </div>

                  {/* Grid de campos */}
                  <div className="profile-fields-grid">
                    {/* BIO */}
                    <div
                      className={`profile-field-card span-2 ${editingField === 'bio' ? 'editing' : ''}`}
                      onClick={() => handleFieldClick('bio')}
                    >
                      <span className="field-icon"><FileText size={18} /></span>
                      <div className="field-body">
                        <span className="field-label">{t("profilePage.bioLabel")}</span>
                        {editingField === 'bio' ? (
                          <textarea
                            value={formData.bio}
                            onChange={(e) => handleInputChange(e, 'bio')}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSaveChanges();
                              }
                            }}
                            className="edit-input"
                            disabled={updateLoading}
                            autoFocus
                          />
                        ) : (
                          <span className="field-value">{formData.bio || t("profilePage.addBioPlaceholder")}</span>
                        )}
                      </div>
                    </div>

                    {/* USERNAME (solo lectura) */}
                    <div className="profile-field-card readonly">
                      <span className="field-icon"><AtSign size={18} /></span>
                      <div className="field-body">
                        <span className="field-label">{t("profilePage.username")}</span>
                        <span className="field-value">{userProfile.username || '—'}</span>
                      </div>
                    </div>

                    {/* EMAIL (solo lectura) */}
                    <div className="profile-field-card readonly">
                      <span className="field-icon"><Mail size={18} /></span>
                      <div className="field-body">
                        <span className="field-label">{t("profilePage.email")}</span>
                        <span className="field-value">{userProfile.email || '—'}</span>
                      </div>
                    </div>

                    {/* PHONE */}
                    <div
                      className={`profile-field-card ${editingField === 'phone_number' ? 'editing' : ''}`}
                      onClick={() => handleFieldClick('phone_number')}
                    >
                      <span className="field-icon"><Phone size={18} /></span>
                      <div className="field-body">
                        <span className="field-label">{t("profilePage.phone")}</span>
                        {editingField === 'phone_number' ? (
                          <input
                            type="tel"
                            value={formData.phone_number}
                            onChange={(e) => handleInputChange(e, 'phone_number')}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveChanges();
                              }
                            }}
                            placeholder="+15551234567"
                            className="edit-input"
                            disabled={updateLoading}
                            autoFocus
                          />
                        ) : (
                          <span className="field-value">{formData.phone_number || t("profilePage.addPhonePlaceholder")}</span>
                        )}
                      </div>
                    </div>

                    {/* COUNTRY */}
                    <div
                      className={`profile-field-card ${editingField === 'country' ? 'editing' : ''}`}
                      onClick={() => handleFieldClick('country')}
                    >
                      <span className="field-icon"><MapPin size={18} /></span>
                      <div className="field-body">
                        <span className="field-label">{t("profilePage.country")}</span>
                        {editingField === 'country' ? (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Select
                              options={countryOptions}
                              value={selectedCountry}
                              onChange={handleCountryChange}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveChanges();
                                }
                              }}
                              isDisabled={updateLoading}
                              placeholder={t("profilePage.selectCountryPlaceholder")}
                              styles={customSelectStyles}
                            />
                          </div>
                        ) : (
                          <span className="field-value">{formData.country || t("profilePage.selectCountryPlaceholder")}</span>
                        )}
                      </div>
                    </div>

                    {/* BIRTH DATE */}
                    <div
                      className={`profile-field-card ${editingField === 'birth_date' ? 'editing' : ''}`}
                      onClick={() => handleFieldClick('birth_date')}
                    >
                      <span className="field-icon"><Calendar size={18} /></span>
                      <div className="field-body">
                        <span className="field-label">{t("profilePage.birthDate", "Birth Date")}</span>
                        {editingField === 'birth_date' ? (
                          <input
                            type="date"
                            value={formData.birth_date}
                            onChange={(e) => handleInputChange(e, 'birth_date')}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveChanges();
                              }
                            }}
                            className="edit-input"
                            disabled={updateLoading}
                            autoFocus
                          />
                        ) : (
                          <span className="field-value">{formData.birth_date || t("profilePage.addBirthDate", "Add birth date")}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mensaje de error */}
                  {updateError && (
                    <div className="error-message" style={{ marginTop: '12px' }}>
                      {updateError}
                    </div>
                  )}

                  {/* Botón guardar cambios */}
                  {showSaveButton && (
                    <div className="save-changes-container">
                      <button
                        className="profile-button save-changes-button"
                        onClick={handleSaveChanges}
                        disabled={updateLoading}
                      >
                        {updateLoading
                          ? t("profilePage.saving")
                          : t("profilePage.saveChanges")
                        }
                      </button>
                    </div>
                  )}
                    </div>
                  </div>
                )}
      </div>

      {/* Avatar change modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-new-design" onClick={(e) => e.stopPropagation()}>
            {/* Modal Title */}
            <h2 className="modal-new-title">
              {modalTab === 'upload'
                ? t("profilePage.modal.uploadPhotoTitle")
                : t("profilePage.modal.generatePhotoTitle")
              }
            </h2>

            {/* Tabs (Upload / Generate) */}
            <div className="modal-new-tabs">
              <div className="modal-new-tab-container">
                <button
                  className={`modal-new-tab-btn ${modalTab === 'upload' ? 'active-tab' : ''}`}
                  onClick={() => {
                    setModalTab('upload');
                    setGeneratedImageUrl(null);
                    setAvatarPrompt('');
                    setGenerateError(null);
                  }}
                >
                  {t("profilePage.modal.uploadTab")}
                </button>
                <button
                  className={`modal-new-tab-btn ${modalTab === 'generate' ? 'active-tab' : ''}`}
                  onClick={() => {
                    setModalTab('generate');
                    setGeneratedImageUrl(null);
                    setAvatarPrompt('');
                    setGenerateError(null);
                  }}
                >
                  {t("profilePage.modal.generateTab")}
                </button>
              </div>
            </div>

            {/* Tab content */}
            <div className="modal-new-content">
              {/* Upload Tab */}
              {modalTab === 'upload' && (
                <div className="modal-upload-section">
                  {!previewUrl ? (
                    <>
                      <div
                        className="modal-upload-dropzone"
                        onDrop={handleDrop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div className="upload-cloud-icon">☁</div>
                        <p className="upload-instruction">
                          {t("profilePage.modal.dropImageHere")}
                        </p>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                      </div>

                      <button
                        className="upload-select-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={avatarUploading}
                      >
                        {avatarUploading
                          ? t("profilePage.modal.uploading")
                          : t("profilePage.modal.selectFromComputer")
                        }
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="upload-preview-container">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="upload-preview-image"
                        />
                      </div>

                      <div className="generated-buttons">
                        <button
                          className="confirm-generate-btn"
                          onClick={confirmUpload}
                          disabled={avatarUploading}
                        >
                          {avatarUploading
                            ? t("profilePage.modal.saving")
                            : t("profilePage.modal.confirmBtn")
                          }
                        </button>
                        <button
                          className="select-another-btn"
                          onClick={cancelUpload}
                        >
                          {t("profilePage.modal.selectAnotherBtn")}
                        </button>
                      </div>
                    </>
                  )}

                  {/* Upload error message */}
                  {uploadError && (
                    <p className="error-message">{uploadError}</p>
                  )}
                </div>
              )}

              {/* Generate Tab */}
              {modalTab === 'generate' && (
                <div className="modal-generate-section">
                  {!generatedImageUrl ? (
                    <>
                      <label className="generate-label">
                        {t("profilePage.modal.promptLabel")}
                      </label>
                      <textarea
                        className="generate-textarea"
                        placeholder={t("profilePage.modal.placeholderPrompt")}
                        value={avatarPrompt}
                        onChange={(e) => {
                          setAvatarPrompt(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                        disabled={generateLoading}
                      />
                      <button
                        className="generate-button"
                        onClick={handleGenerateAvatar}
                        disabled={generateLoading || !avatarPrompt.trim()}
                      >
                        {generateLoading
                          ? t("profilePage.modal.generating")
                          : t("profilePage.modal.generateButton")
                        }
                      </button>
                      {generateError && (
                        <p className="error-message">
                          {generateError}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="generated-image-container">
                      <img
                        src={generatedImageUrl}
                        alt="AI Generated"
                        className="generated-avatar"
                      />
                      <div className="generated-buttons">
                        <button
                          className="confirm-generate-btn"
                          onClick={handleSaveGeneratedImage}
                          disabled={avatarUploading}
                        >
                          {avatarUploading
                            ? t("profilePage.modal.saving")
                            : t("profilePage.modal.confirmBtn")
                          }
                        </button>
                        <button
                          className="select-another-btn"
                          onClick={() => {
                            setGeneratedImageUrl(null);
                            setAvatarPrompt('');
                          }}
                        >
                          {t("profilePage.modal.selectAnotherBtn")}
                        </button>
                      </div>
                    </div>
                  )}
                  {uploadError && <p className="error-message">{uploadError}</p>}
                </div>
              )}
            </div>

            {/* Close button */}
            <button className="modal-new-close" onClick={() => setShowModal(false)}>
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}
